import type { SortTeamColumns, TeamsQueryParams } from '@library/types/teams';
import { useEffect, useState } from 'react';
import { DEFAULT_TEAM_DATA } from '@bo-constants/teams';
import { DEFAULT_TEAM_SORT } from '@library/constants/sorting';
import { DateTime } from 'luxon';
import { Loading } from '@library/components/Loading';
import { TeamsTable } from '@bo-components/teams/TeamsTable';
import UtilDashboardHeader from '@bo-components/common/UtilDashboardHeader';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { usePerPage } from '@bo-hooks/usePerPage';
import { useSearchParams } from 'react-router-dom';
import { useTeams } from '@bo-hooks/utilsDashboards/useTeams';
import useUtilDashboard from '@bo-hooks/utilsDashboards/useUtilDashboard';

export const TeamsDashboard = () => {
  const [searchParams] = useSearchParams();

  const {
    durations,
    handleSearch,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue
  } = useDashBoard({
    initialStartDate: DateTime.fromISO('2018-01-01'),
    dashboardName: 'TEAMS'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<SortTeamColumns>(DEFAULT_TEAM_SORT);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentItemId, setCurrentItemId] = useState('');

  const { handleSelectPerPage, selectedPerPage } = usePerPage();

  const { id: leagueId } = leagueInfo ?? {};
  const teamsQueryParams = {
    leagueId,
    page: currentPage,
    perPage: selectedPerPage,
    searchValue,
    sortBy: sort.sortBy,
    sortDir: sort.sortDirection
  } as TeamsQueryParams;

  const { isError, isLoading, teams, totalPages } = useTeams({
    teamsQueryParams,
    leaguesList,
    currentItemId,
    setSubmitMessage
  });

  const {
    allItemsChecked,
    currentItemData,
    isDetailsModalOpen,
    idsForCSV,
    isGeneratingCSV,
    handleClickAllItemsCheckbox,
    handleClickCheckbox,
    handleCSVExportClick,
    handleAddNewItemClick,
    handleClickRow
  } = useUtilDashboard({
    data: teams,
    path: 'models/teams',
    defaultItemData: DEFAULT_TEAM_DATA,
    setSubmitMessage
  });

  useEffect(() => {
    if (currentItemData.id) {
      setCurrentItemId(currentItemData.id);
    }
  }, [currentItemData.id]);

  // Check for game type ID in query parameters and open the corresponding game type
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && teams.length > 0 && !isDetailsModalOpen) {
      const team = teams.find((t) => t.id === id);
      if (team) {
        handleClickRow(id);
      }
    }
  }, [searchParams, teams, handleClickRow, isDetailsModalOpen]);

  // TODO: After we add modal
  // const hasCreateOrEditTeams = useSelector(selectHasEditTeams);

  // TODO: Remove submitMessage, only here for linter
  if (isLeagueLoading || isLoading || submitMessage) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <div className="h-full w-auto">
      <UtilDashboardHeader
        isCreateAllowed={false}
        isCSVExportDisabled={idsForCSV.length === 0}
        isGeneratingCSV={isGeneratingCSV}
        handleCSVExportClick={handleCSVExportClick}
        handleCreateNewItemClick={handleAddNewItemClick}
        pageTitle="Teams"
      />
      <TeamsTable
        teams={teams}
        totalPages={totalPages}
        handleSelectPerPage={handleSelectPerPage}
        selectedPerPage={selectedPerPage}
        durations={durations}
        handleSearch={handleSearch}
        handleSelectLeague={handleSelectLeague}
        searchValue={searchValue}
        leaguesList={leaguesList}
        selectedLeagueId={leagueId}
        isError={isError}
        allItemsChecked={allItemsChecked}
        handleClickAllItemsCheckbox={handleClickAllItemsCheckbox}
        handleClickCheckbox={handleClickCheckbox}
        handleClickRow={handleClickRow}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        sort={sort}
        setSort={setSort}
      />
    </div>
  );
};
